/**
 * Copyright 2020 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getRewardsBalance } from '@/utils/getRewardsBalance';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import type { PageViewCall, PageSpecificAnalyticsDataType } from '@/types/analytics';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRewardDetails } from '@/features/rewards/api/getRewardDetails';
import { getKiboDecisionFromCache } from '@/features/kibo/api/getKiboDecision';
import { useQueryClient } from '@tanstack/react-query';
import { sessionStorage } from '@/utils/sessionStorage';
import { persistXFusionQueryId } from '@/utils/xFusionQueryIdHelpers';
import { trackAddVehicle } from '../trackAddVehicle';
import { STAND_ALONE_USER_TYPE } from '@/features/header';
import { useMyProfile } from '@/features/myProfile/api/getMyProfileV2';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import {
  getStoredInteractionLocation,
  clearInteractionLocation,
} from '@/features/ymme/utils/ymmeLocationHelper';
import {
  formatBaseVehicle,
  formatVehicleInformation,
  formatVehicleQuestions,
  formatAcesYMME,
  buildVehicleURL,
} from './vehicleFormatter';
import environmentConfig from '@/config/environment';
import { getDecodedCookiesSync, setCookie } from '../cookie';
import { useLoginConfirmation } from '@/features/account/hooks/useLoginConfirmation';
import { getAccountType } from './getAccountType';
import { cleanObject } from '../cleanObject';
import { getVerificationAnalytics } from './getVerificationAnalytics';
import { useAppData } from '@/hooks/useAppData';

export const usePageViewCall = (): PageViewCall => {
  const { showRewardSigninPopup } = useLoginConfirmation();
  const locale = useLocale();
  const acesFlags = useAcesFlags();
  const isUS = locale === countryCodes.us;
  const { data: headerData } = useHeaderData();
  const { data: storeDetails } = useStoreDetailsData();
  const { data: userData } = useMyProfile();
  const userType = userData?.userType;
  const isStandAloneUser = userType === Number(STAND_ALONE_USER_TYPE);
  const { appData } = useAppData();
  const { userAuthenticated } = appData;
  const { data: rewardCardNumber } = useRewardDetails();
  const router = useLegacyRouter();
  const searchRedirectText =
    typeof router.query.searchText === 'string' ? router.query.searchText : '';
  const queryClient = useQueryClient();
  const isPersistentLogin = headerData?.myAccountMap?.autoLogin ?? 'false';
  const isClient = typeof document !== 'undefined';
  const cookies = isClient ? getDecodedCookiesSync(['autoLogin']) : null;
  const isAutoLogin = cookies?.get('autoLogin') ?? 'false';
  const vehiclesInfo = headerData?.vehicleMap;
  const vehicleId = vehiclesInfo?.catalogVehicleId;
  const prevSessionVehicleId = sessionStorage.getItem('previousPreferredVehicleId');
  const isAddByURL =
    (!!router.query.vehicleId &&
      router.query.vehicleId === vehicleId &&
      vehicleId !== prevSessionVehicleId) ||
    !!router.query.makeName;
  const isPDP = router.pathname === '/products/[...slug]';

  return (
    pageSpecificAnalyticsData: PageSpecificAnalyticsDataType,
    pageSpecificRemoveAnalyticsData?: string[] | null,
  ) => {
    const lwTestExperience = pageSpecificAnalyticsData?.lwTestExperience;
    const productRank = pageSpecificAnalyticsData?.productRank;
    const xFusionQueryId = pageSpecificAnalyticsData?.xFusionQueryId;
    const allSearchTerms = window.utag_data?.allSearchTerms;
    const accountType = getAccountType(userAuthenticated, userData, router.query);
    const loginInteractionMethod = window.utag_data?.loginInteractionMethod;
    const loginMethod = window.utag_data?.loginMethod;
    const authenticationStatus = appData.userAuthenticated;
    const rewardsCustomerStatus = headerData?.myAccountMap?.rewardsId?.length ? true : false;
    const profileId = headerData?.myAccountMap?.profileId ?? '';
    const rewardsId = headerData?.myAccountMap?.rewardsId ?? '';
    const jSessionId = window.utag_data?.jSessionId ?? headerData?.apiResponseHeaders?.token_id;
    const eCookieId = window.utag_data?.eCookieId;
    const preferredZipCode = storeDetails?.zip ?? '';
    const preferredStoreId = storeDetails?.storeNumber ?? '';
    const persistentLogin = isPersistentLogin;
    const { vehicleMap } = headerData ?? {};
    const preferredVehicleYear = vehicleMap?.year ?? '';
    const preferredVehicleMake = vehicleMap?.make ?? '';
    const preferredVehicleModel = vehicleMap?.model ?? '';
    const preferredVehicleEngine =
      vehicleMap?.vehicleName
        .replace(`${preferredVehicleYear} ${preferredVehicleMake} ${preferredVehicleModel}`, '')
        ?.trim() ?? '';
    const preferredVehicleId = vehicleMap?.catalogVehicleId ?? '';
    const userVehicleCount = window.utag_data?.userVehicleCount ?? '0';
    const serverInfo = window.utag_data?.serverInfo;
    const device = appData.deviceType;
    const userType = headerData?.myAccountMap?.userType ?? ''; // we can get some of the "previous" information from the utag data
    const previousPageName = window.utag_data?.pageName ?? '';
    const previousPageType = window.utag_data?.pageType ?? '';
    //To determine whether the certona script call loaded correctly or  not.
    const certonaCalled = !window?.certonaNotCalled;
    const previousPageSiteSection = window.utag_data?.siteSection ?? '';
    const pageURL = window.utag_data?.pageURL ?? '';
    const productRecommendationInteraction =
      window.utag_data?.productRecommendationInteraction ?? '';
    const productRecommendationType = window.utag_data?.productRecommendationType ?? '';
    const previousPageURL = window.utag_data?.previousPageURL ?? '';
    const referrer = window.utag_data?.referrer ?? '';
    const nddMarket = headerData?.analyticsData?.nddMarket ?? '';
    const pageLazyLoad = pageSpecificAnalyticsData.pageLazyLoad ?? false;
    const searchMethod = pageSpecificAnalyticsData.searchMethod ?? '';
    const nddHub = headerData?.analyticsData?.nddHubStore ?? '';
    const nddStore = headerData?.analyticsData?.nddStore ?? '';
    const currentNumberOfCredits = rewardCardNumber?.currentNumberOfCredits ?? '';
    const rewardsBalance = rewardCardNumber?.rewardBalance ?? '';
    const availableRewardBalance = rewardsBalance ? getRewardsBalance(rewardsBalance) : '';
    const hashedEId = window.utag_data?.hashedEId ?? '';
    const revenueBucket = window.utag_data?.revenueBucket ?? '';
    const typeaheadValue = sessionStorage.getItem('typeAheadValue');
    const pbsSearchEngine = sessionStorage.getItem('pbs-search-engine');
    const visualPartTypeDisplayed = pageSpecificAnalyticsData?.visualPartTypeDisplayed;
    const partTypeFiltersAvailable = pageSpecificAnalyticsData?.partTypeFiltersAvailable;
    const internalCampaignInteraction = pageSpecificAnalyticsData?.internalCampaignInteraction;

    const kiboData = getKiboDecisionFromCache(queryClient);
    const experienceNames = kiboData?.actions
      ? Object.values(kiboData.actions).map(
          (action) =>
            `${action.impressionReporting[0].experience_label} | ${action.impressionReporting[0].variant_label}`,
        )
      : undefined;

    const baseVehicle = formatBaseVehicle(vehicleMap);
    const vehicleQuestions = formatVehicleQuestions(vehicleMap);
    const vehicleInformation = formatVehicleInformation(vehicleMap);
    const releaseRegex = environmentConfig.BUILD_BRANCH?.match(
      /(?:\s*release\/|v-)(\d+\.\d+\.\d+)/,
    );
    const releaseVersion = releaseRegex ? releaseRegex[1] : '';

    const acesYMME = formatAcesYMME(vehicleMap);

    const isVehicleSet = Boolean(vehicleMap?.vehicleId || vehicleMap?.vehicleName);

    const vehicleSetStatus = isVehicleSet ? 'True' : 'False';
    const vehicleURL = buildVehicleURL(vehicleMap);

    const personalization = isUS ? userData?.personalization : undefined;

    const personalizationPayload = cleanObject(personalization ?? {});

    const visitCountAnalytics = userData
      ? {
          customerSiteLoginCount: userData.webVisitCount,
          customerAppLoginCount: userData.appVisitCount,
        }
      : {};

    const hasCalledPageViewOnce = sessionStorage.getItem('userPageViewAnalyticsCalled');
    const shouldSendFirstTimePageViewAnalytics =
      !!userData && userAuthenticated && (!hasCalledPageViewOnce || isAutoLogin === 'true');
    const firstTimePageViewAnalytics = shouldSendFirstTimePageViewAnalytics
      ? getVerificationAnalytics(userData)
      : {};
    if (shouldSendFirstTimePageViewAnalytics) {
      sessionStorage.setItem('userPageViewAnalyticsCalled', 'true');
    }

    const data = {
      allSearchTerms,
      internalCampaignInteraction,
      authenticationStatus,
      availableRewardBalance,
      currentNumberOfCredits,
      eCookieId,
      rewardsCustomerStatus,
      profileId,
      rewardsId,
      jSessionId,
      preferredZipCode,
      preferredStoreId,
      preferredVehicleYear,
      preferredVehicleMake,
      preferredVehicleModel,
      preferredVehicleEngine,
      preferredVehicleId,
      userVehicleCount,
      serverInfo,
      device,
      userType,
      previousPageName,
      previousPageType,
      previousPageSiteSection,
      pageURL,
      previousPageURL,
      referrer,
      nddMarket,
      nddHub,
      nddStore,
      hashedEId,
      revenueBucket,
      pageLazyLoad,
      accountType,
      loginInteractionMethod,
      loginMethod,
      searchMethod,
      experienceNames,
      lwTestExperience,
      certonaCalled,
      visualPartTypeDisplayed,
      persistentLogin,
      partTypeFiltersAvailable,
      ACESPIESstatus: true,
      releaseVersion,
      ...personalizationPayload,
      ...visitCountAnalytics,
      ...firstTimePageViewAnalytics,
      vehicleSetStatus,
      ...(acesYMME.length ? { aces_ymme: acesYMME } : {}),
      ...(isVehicleSet && {
        baseVehicle,
        vehicleQuestions,
        Engine: vehicleMap?.engine || '',
        Submodel: vehicleMap?.subModel || '',
        vehicleInformation,
      }),

      ...(isPDP && {
        productRecommendationInteraction,
      }),
      ...(isPDP && {
        productRecommendationType,
      }),
      ...(vehicleURL && { vehicleURL }),
      ...(!!productRank && {
        productRank,
      }),
      ...(!!xFusionQueryId && {
        xFusionQueryId,
      }),
      ...(!!searchRedirectText && {
        searchKeyword: searchRedirectText,
        eventType: 'successfulSearch',
      }),
      ...(!!typeaheadValue && {
        searchKeyword: typeaheadValue,
        eventType: 'successfulSearch',
      }),
      ...(!!pbsSearchEngine && { 'pbs-search-engine': pbsSearchEngine }),
      ...pageSpecificAnalyticsData,
      productFindingMethod: pageSpecificAnalyticsData.productFindingMethod ?? '',
    };

    if (
      !data.lwTestExperience ||
      (data.pageType.includes('shelf') && !router.asPath.includes('search'))
    ) {
      delete data.lwTestExperience;
    }

    if (
      !data.eventType ||
      (!router.asPath.includes('searchresult') &&
        !router.asPath.includes('redirect') &&
        !router.asPath.includes('checkout') &&
        data.eventType !== 'loadMore' &&
        data.eventType !== 'updateVehicle' &&
        data.eventType !== 'expandResults' &&
        !data.selectedFilterValue)
    ) {
      delete data.eventType;
      delete window.utag_data?.['eventType'];
    }

    if (!data.visualPartTypeDisplayed || !router.asPath.includes('searchresult')) {
      delete data.visualPartTypeDisplayed;
    }

    if (
      data.partTypeFiltersAvailable == null ||
      (!data.pageType.includes('shelf') &&
        !data.pageType.includes('Brand Profile') &&
        !router.asPath.includes('searchresult'))
    ) {
      delete data.partTypeFiltersAvailable;
    }

    // persist xFusionQueryId so it can be passed via /item request
    persistXFusionQueryId(xFusionQueryId);

    // Remove originalPartTypeIdList when ACES is enabled
    const analyticsDataToRemove = [...(pageSpecificRemoveAnalyticsData || [])];

    analyticsDataToRemove.push('originalPartTypeIdList');

    if (analyticsDataToRemove.length) {
      analyticsDataToRemove.forEach((removeItem) => {
        // @ts-expect-error refine type
        delete data[removeItem];
        if (window.utag_data) {
          delete window.utag_data[removeItem];
        }
      });
    }

    const storedInteractionLocation = getStoredInteractionLocation();

    if (storedInteractionLocation === 'Needs more info') {
      data.eventType = 'updateVehicle';
      data.vehicle_interaction_location = 'Need more info';
      clearInteractionLocation();
    }

    window.utag_data = data;
    window.utag?.view(data);
    sessionStorage.removeItem('typeAheadValue');
    sessionStorage.removeItem('pbs-search-engine');
    sessionStorage.removeItem('googleRetailRecommendationAttributionToken');
    if (isAutoLogin === 'true') {
      if (isUS && isStandAloneUser) {
        showRewardSigninPopup();
      }
      void setCookie('autoLogin', 'false');
    }
    if (isAddByURL) {
      // pass acesEnabled to enable ACES analytics (see PDP-626)
      trackAddVehicle({ isAddByURL, headerData, acesFlags });
    }
  };
};
